// src/pages/TaskBoard.js - FINAL (Con Filtro de Tareas Internas)
import React, { lazy, Suspense, useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TextField,
  MenuItem,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  alpha,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  Stack,
    Snackbar,
  Avatar,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ChecklistIcon from "@mui/icons-material/Checklist";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import taskService from '../services/taskService';
import userService from '../services/userService';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useSocketContext } from '../context/SocketContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNativeMobilePlatform } from '../utils/platform';
import TaskActivityOverview from '../components/TaskActivityOverview';
import TaskActivityBoard, { TaskActivityCard } from '../components/TaskActivityBoard';
import TaskMyDay from '../components/TaskMyDay';
import TaskQuickProgressDialog from '../components/TaskQuickProgressDialog';
import TaskDayApprovals from '../components/TaskDayApprovals';
import { activityFilterLabel, activityDueState, buildMyDay, filterActivityScope, matchesActivityFilter, normalizeActivityStatus, summarizeActivities } from '../utils/taskActivityModel';

const NativeTaskBoard = lazy(() => import('../components/mobile/NativeTaskBoard'));


const STATUS_COLUMNS = [
  { id: "todo", title: "Pendiente" },
  { id: "in_progress", title: "En curso" },
  { id: "blocked", title: "En espera / bloqueado" },
  { id: "done", title: "Terminado" },
];

const STATUS_STYLES = {
  todo: { accent: "#607d8b", softLight: "#f3f5f7" },
  in_progress: { accent: "#0288d1", softLight: "#e8f4fd" },
  blocked: { accent: "#d32f2f", softLight: "#ffebee" },
  done: { accent: "#2e7d32", softLight: "#e8f5e9" },
};

const mapStatusToUI = normalizeActivityStatus;

const parseTaskDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value);
  const dateKey = raw.slice(0, 10);
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(dateKey)
    ? new Date(`${dateKey}T12:00:00`)
    : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTaskDate = (value) => {
  const parsed = parseTaskDate(value);
  return parsed
    ? new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).format(parsed)
    : 'Sin fecha';
};

const formatHistoryDateTime = (value) => {
  const parsed = new Date(value || 0);
  return Number.isNaN(parsed.getTime())
    ? 'Fecha no disponible'
    : new Intl.DateTimeFormat('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
    }).format(parsed);
};

const getStatusTitle = (status) => STATUS_COLUMNS.find((item) => item.id === status)?.title || status || 'Sin estatus';

const HISTORY_EVENT_LABELS = {
  created: 'creó el pendiente',
  progress: 'registró un avance',
  details_updated: 'actualizó los datos generales',
  comment: 'agregó un comentario'
};

const parseHistoryChanges = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (error) { return {}; }
};

function TaskProjectAutocomplete({ projects, value, onChange, loading = false }) {
  const options = useMemo(() => [
    { id: '', name: 'Internal / No Project', internal: true },
    ...projects.map((project) => ({ ...project, id: String(project.id) }))
  ], [projects]);

  const selectedProject = options.find(
    (option) => String(option.id) === String(value || '')
  ) || options[0];

  return (
    <Autocomplete
      fullWidth
      disableClearable
      autoHighlight
      openOnFocus
      options={options}
      value={selectedProject}
      loading={loading}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, selected) => String(option.id) === String(selected.id)}
      onChange={(_, option) => onChange(option?.id || '')}
      noOptionsText="No projects found"
      slotProps={{
        paper: {
          sx: {
            width: 'min(520px, calc(100vw - 24px))',
            maxWidth: 'calc(100vw - 24px)'
          }
        },
        listbox: {
          sx: {
            maxHeight: 'min(46dvh, 360px)',
            '& .MuiAutocomplete-option': {
              alignItems: 'flex-start',
              minHeight: 48,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere'
            }
          }
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.internal ? 'internal-task' : option.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: option.internal ? 700 : 500, overflowWrap: 'anywhere' }}>
              {option.name}
            </Typography>
            {option.internal && (
              <Typography variant="caption" color="text.secondary">
                Task not linked to a project
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Project"
          placeholder="Search by project name"
          helperText="Type to search or choose an internal task"
        />
      )}
    />
  );
}

export default function TaskBoard({ projectId = null, embedded = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const nativeMobile = isNativeMobilePlatform();
  const isDarkMode = theme.palette.mode === 'dark';
  const { projects, loading: projectsLoading, userRole } = useProjects();
  const { user } = useAuth();
  const { socket, connected } = useSocketContext();
  const location = useLocation();
  const navigate = useNavigate();
  const progressStatusId = React.useId();

  const [tasks, setTasks] = useState([]);
  const taskLoadRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    q: "",
    assignee: "",
    priority: "",
    status: "active",
    project: "",
  });
  const [drawerTask, setDrawerTask] = useState(null);
  const [editedTask, setEditedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [newTask, setNewTask] = useState({
    title: "",
    project_id: "", 
    assigned_to: "",
    status: "todo",
    priority: "medium",
    due_date: null,
    description: "",
    initial_comment: "",
  });

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const historyRequest = useRef(0);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [progressUpdate, setProgressUpdate] = useState({
    status: 'todo',
    expected_date: null,
    comment: ''
  });
  const [savingProgress, setSavingProgress] = useState(false);

  const [users, setUsers] = useState([]);
  const [, setLoadingUsers] = useState(false);
  const canSeeAllTasks = userRole === 'admin';
  const canDeleteTasks = userRole === 'admin';
  const [viewMode, setViewMode] = useState(canSeeAllTasks ? 'all' : 'mine');

  const [notification, setNotification] = useState(null);
  const [presentation, setPresentation] = useState('board');
  const [workspace, setWorkspace] = useState('day');
  const [quickTask, setQuickTask] = useState(null);
  const showMyDay = !projectId && !embedded && workspace === 'day';
  const [activityNow, setActivityNow] = useState(() => new Date());

  useEffect(() => {
    const updateClock = () => setActivityNow(new Date());
    const timer = setInterval(updateClock, 60000);
    window.addEventListener('focus', updateClock);
    document.addEventListener('visibilitychange', updateClock);
    return () => { clearInterval(timer); window.removeEventListener('focus', updateClock); document.removeEventListener('visibilitychange', updateClock); };
  }, []);

  const loadTasks = useCallback(async () => {
    const request = ++taskLoadRequest.current;
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (projectId) params.project_id = projectId;
      if (!canSeeAllTasks || viewMode === 'mine') params.assigned_to = user?.id;
      const response = await taskService.getTasks(params);
      const transformedTasks = (response.tasks || []).map((task) => ({
        ...task,
        id: task.id.toString(),
        status: mapStatusToUI(task.status)
      }));
      if (request === taskLoadRequest.current) setTasks(transformedTasks);
    } catch (err) {
      console.error('âŒ Error loading tasks:', err);
      if (request === taskLoadRequest.current) setError('No se pudieron cargar los pendientes: ' + err.message);
    } finally {
      if (request === taskLoadRequest.current) setLoading(false);
    }
  }, [canSeeAllTasks, projectId, user?.id, viewMode]);

  useEffect(() => {
    if (projectId) {
      setFilters(prev => ({ ...prev, project: projectId.toString() }));
      setNewTask(prev => ({ ...prev, project_id: projectId.toString() }));
    }
  }, [projectId]);

  useEffect(() => {
    if (!canSeeAllTasks && viewMode !== 'mine') {
      setViewMode('mine');
    }
  }, [canSeeAllTasks, viewMode]);

  useEffect(() => {
    loadTasks();
  }, [projectId, viewMode, canSeeAllTasks, user?.id, loadTasks]);

  useEffect(() => {
    const loadUsersData = async () => {
      try {
        setLoadingUsers(true);
        const response = await userService.getUsers();
        setUsers(response.users || []);
      } catch (err) {
        console.error('❌ Error loading users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsersData();
  }, []);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const requestedTaskId = location.state?.openTaskId || currentParams.get('taskId');
    if (requestedTaskId && loading) return;

    let consumed = false;
    if (location.state?.openDialog) {
      setOpenDialog(true);
      consumed = true;
    }
    if (requestedTaskId) {
      const requestedTask = tasks.find((task) => String(task.id) === String(requestedTaskId));
      if (requestedTask) setDrawerTask(requestedTask);
      consumed = true;
    }
    if (consumed) {
      currentParams.delete('taskId');
      currentParams.delete('view');
      currentParams.delete('action');
      const nextSearch = currentParams.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true, state: null }
      );
    }
  }, [loading, location.pathname, location.search, location.state, navigate, tasks]);

  useEffect(() => {
    if (drawerTask) {
      setEditedTask({ ...drawerTask });
      setProgressUpdate({
        status: mapStatusToUI(drawerTask.status),
        expected_date: parseTaskDate(drawerTask.due_date),
        comment: ''
      });
      loadHistory(drawerTask.id);
    } else {
      historyRequest.current += 1;
      setLoadingHistory(false);
      setHistory([]);
      setHistoryFilter('all');
      setEditedTask(null);
    }
  }, [drawerTask]);

  // Socket.IO Logic
  useEffect(() => {
    if (!socket || !connected) return;

    const handleTaskUpdated = (updatedTask) => {
      if (projectId && updatedTask.project_id !== parseInt(projectId)) return;
      setTasks((prevTasks) => {
        const index = prevTasks.findIndex(t => t.id === updatedTask.id.toString());
        if (index !== -1) {
          const newTasks = [...prevTasks];
          newTasks[index] = { ...updatedTask, id: updatedTask.id.toString(), status: mapStatusToUI(updatedTask.status) };
          return newTasks;
        }
        return prevTasks;
      });
      setDrawerTask((currentDrawerTask) => {
        if (currentDrawerTask && currentDrawerTask.id === updatedTask.id.toString()) {
          return { ...updatedTask, id: updatedTask.id.toString(), status: mapStatusToUI(updatedTask.status) };
        }
        return currentDrawerTask;
      });
    };

    const handleTaskStatusChanged = ({ task, oldStatus, newStatus, changedBy }) => {
      if (projectId && task.project_id !== parseInt(projectId)) return;
      setNotification({ message: `${changedBy} moved "${task.title}" to ${newStatus}`, severity: 'info' });
      setTasks((prevTasks) => {
        const index = prevTasks.findIndex(t => t.id === task.id.toString());
        if (index !== -1) {
          const newTasks = [...prevTasks];
          newTasks[index] = { ...task, id: task.id.toString(), status: mapStatusToUI(task.status) };
          return newTasks;
        }
        return prevTasks;
      });
    };

    const handleTaskAssigned = ({ task, assignedBy }) => {
      if (projectId && task.project_id !== parseInt(projectId)) return;
      setNotification({ message: `${assignedBy} assigned you "${task.title}"`, severity: 'success' });
      setTasks((prevTasks) => {
        const index = prevTasks.findIndex(t => t.id === task.id.toString());
        const mappedTask = { ...task, id: task.id.toString(), status: mapStatusToUI(task.status) };
        if (index !== -1) {
          const newTasks = [...prevTasks];
          newTasks[index] = mappedTask;
          return newTasks;
        } else {
          return [...prevTasks, mappedTask];
        }
      });
    };

    const handleTaskCreated = (newTask) => {
      if (projectId && newTask.project_id !== parseInt(projectId)) return;
      if (userRole === 'admin' || newTask.assigned_to === user.id) {
        setTasks((prevTasks) => [...prevTasks, { ...newTask, id: newTask.id.toString(), status: mapStatusToUI(newTask.status) }]);
      }
    };

    const handleTaskDeleted = ({ taskId }) => {
      setTasks((prevTasks) => prevTasks.filter(t => t.id !== taskId.toString()));
      setDrawerTask((currentDrawerTask) => {
        if (currentDrawerTask && currentDrawerTask.id === taskId.toString()) return null;
        return currentDrawerTask;
      });
    };

    socket.on('taskUpdated', handleTaskUpdated);
    socket.on('taskStatusChanged', handleTaskStatusChanged);
    socket.on('taskAssigned', handleTaskAssigned);
    socket.on('taskCreated', handleTaskCreated);
    socket.on('taskDeleted', handleTaskDeleted);

    return () => {
      socket.off('taskUpdated', handleTaskUpdated);
      socket.off('taskStatusChanged', handleTaskStatusChanged);
      socket.off('taskAssigned', handleTaskAssigned);
      socket.off('taskCreated', handleTaskCreated);
      socket.off('taskDeleted', handleTaskDeleted);
    };
  }, [socket, connected, userRole, user, projectId]);

  // eslint-disable-next-line no-unused-vars
  const loadTasksLegacy = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (projectId) params.project_id = projectId;
      if (!canSeeAllTasks || viewMode === 'mine') params.assigned_to = user?.id;
      const response = await taskService.getTasks(params);
      const transformedTasks = (response.tasks || []).map(t => ({ ...t, id: t.id.toString(), status: mapStatusToUI(t.status) }));
      setTasks(transformedTasks);
    } catch (err) {
      console.error('❌ Error loading tasks:', err);
      setError('Error loading tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (taskId) => {
    const request = ++historyRequest.current;
    try {
      setLoadingHistory(true);
      const response = await taskService.getHistory(taskId);
      if (request === historyRequest.current) setHistory(response.history || []);
    } catch (err) {
      console.error('Error loading task history:', err);
      if (request === historyRequest.current) setError(err.response?.data?.message || 'No se pudo cargar el historial del pendiente.');
    } finally {
      if (request === historyRequest.current) setLoadingHistory(false);
    }
  };

  const assignees = useMemo(() => Array.from(new Set(tasks.map((t) => t.assigned_to_name || 'Sin asignar'))), [tasks]);

  const filteredHistory = useMemo(() => history.filter((event) => {
    if (historyFilter === 'progress') return ['progress', 'comment'].includes(event.event_type);
    if (historyFilter === 'changes') return ['created', 'details_updated'].includes(event.event_type);
    return true;
  }), [history, historyFilter]);

  const formatHistoryValue = (field, value) => {
    if (value === undefined || value === null || value === '') return 'Sin valor';
    if (field === 'status') return getStatusTitle(value);
    if (field === 'due_date') return formatTaskDate(value);
    if (field === 'assigned_to') {
      const assignee = users.find((item) => String(item.id) === String(value));
      return assignee?.first_name || assignee?.username || `Usuario #${value}`;
    }
    if (field === 'project_id') {
      const project = projects.find((item) => String(item.id) === String(value));
      return project?.name || `Proyecto #${value}`;
    }
    const text = String(value);
    return text.length > 120 ? `${text.slice(0, 117)}…` : text;
  };

  const activityScope = useMemo(() => filterActivityScope(tasks.filter((task) => (
    ((canSeeAllTasks && viewMode === 'all') || String(task.assigned_to) === String(user?.id))
    && (!projectId || String(task.project_id) === String(projectId))
  )), filters), [tasks, filters, canSeeAllTasks, viewMode, user?.id, projectId]);
  const filtered = useMemo(() => activityScope.filter((task) => matchesActivityFilter(task, filters.status, activityNow)), [activityScope, filters.status, activityNow]);
  const boardStats = useMemo(() => summarizeActivities(activityScope, activityNow), [activityScope, activityNow]);
  const selectActivityFilter = (status) => setFilters((current) => ({ ...current, status }));
  const myDayGroups = useMemo(() => buildMyDay(tasks, user?.id, activityNow), [tasks, user?.id, activityNow]);
  const createPersonalTask = () => {
    setNewTask((current) => ({ ...current, assigned_to: String(user?.id || '') }));
    setOpenDialog(true);
  };
  const applyProgressResult = (response) => {
    const updated = { ...response.task, id: String(response.task.id), status: mapStatusToUI(response.task.status) };
    setTasks((current) => current.map((task) => String(task.id) === updated.id ? updated : task));
    setDrawerTask((current) => current && String(current.id) === updated.id ? updated : current);
    setNotification({ severity: 'success', message: 'Avance registrado y guardado en el historial.' });
  };
  const workspaceSwitch = !projectId && !embedded && <ToggleButtonGroup value={workspace} exclusive onChange={(_, value) => { if (value) setWorkspace(value); }} aria-label="Espacio de actividades" sx={{ my: 1, display: 'flex', flexWrap: 'wrap' }}>
    <ToggleButton value="day" sx={{ minHeight: 44, flex: { xs: 1, sm: 'initial' }, px: 2 }}>Mi día</ToggleButton>
    <ToggleButton value="activities" sx={{ minHeight: 44, flex: { xs: 1, sm: 'initial' }, px: 2 }}>Vista general</ToggleButton>
  </ToggleButtonGroup>;
  const dayView = showMyDay && <TaskMyDay groups={myDayGroups} now={activityNow} onOpen={setDrawerTask} onQuickProgress={setQuickTask} onCreate={createPersonalTask} onRefresh={loadTasks} dataError={Boolean(error)} approvals={<TaskDayApprovals />} />;
  const quickProgressDialog = quickTask && <TaskQuickProgressDialog key={quickTask.id} task={quickTask} onClose={() => setQuickTask(null)} onSaved={applyProgressResult} />;

  const saveNewTask = async () => {
    if (!newTask.title) { setError('El nombre del pendiente es obligatorio.'); return; }
    try {
      const payload = {
        ...newTask,
        project_id: newTask.project_id || null,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null,
      };
      await taskService.createTask(payload);
      setError('');
      setOpenDialog(false);
      setNewTask({ title: "", project_id: projectId || "", assigned_to: "", status: "todo", priority: "medium", due_date: null, description: "", initial_comment: "" });
      loadTasks();
    } catch (err) {
      setError('Error creating task: ' + err.message);
    }
  };

  const handleSaveTask = async () => {
    if (!editedTask) return;
    try {
      await taskService.updateTask(editedTask.id, editedTask);
      setError('');
      setDrawerTask(null);
      loadTasks();
    } catch (err) {
      setError('Error saving task: ' + err.message);
    }
  };

  const handleRecordProgress = async () => {
    if (!drawerTask || !progressUpdate.comment.trim()) {
      setError('Escribe un comentario para registrar el avance.');
      return;
    }

    try {
      setSavingProgress(true);
      const response = await taskService.recordProgress(drawerTask.id, {
        status: progressUpdate.status,
        expected_date: progressUpdate.expected_date
          ? new Date(progressUpdate.expected_date).toISOString().slice(0, 10)
          : null,
        comment: progressUpdate.comment.trim()
      });
      const updatedTask = {
        ...response.task,
        id: String(response.task.id),
        status: mapStatusToUI(response.task.status)
      };

      setTasks((current) => current.map((task) => (
        String(task.id) === String(updatedTask.id) ? updatedTask : task
      )));
      setDrawerTask(updatedTask);
      setEditedTask(updatedTask);
      if (response.history_event) {
        setHistory((current) => (
          current.some((item) => String(item.id) === String(response.history_event.id))
            ? current
            : [response.history_event, ...current]
        ));
      }
      setProgressUpdate((current) => ({ ...current, comment: '' }));
      setError('');
      setNotification({ severity: 'success', message: 'Avance registrado correctamente.' });
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo registrar el avance.');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!drawerTask) return;
    if (!canDeleteTasks) {
      setError('Only admins can delete tasks');
      return;
    }
    if (!window.confirm('Delete this task?')) return;
    try {
      await taskService.deleteTask(drawerTask.id);
      setDrawerTask(null);
      loadTasks();
    } catch (err) {
      setError('Error deleting task: ' + err.message);
    }
  };

  if (loading || projectsLoading) {
    return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  }

  if (nativeMobile) {
    return (
      <>
        <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}><CircularProgress /></Box>}>
          <NativeTaskBoard
          workspaceSwitch={workspaceSwitch}
          dayView={dayView}
          showMyDay={showMyDay}
          projectId={projectId}
          visibleTasks={filtered}
          boardStats={boardStats}
          selectActivityFilter={selectActivityFilter}
          filters={filters}
          setFilters={setFilters}
          projects={projects}
          assignees={assignees}
          canSeeAllTasks={canSeeAllTasks}
          viewMode={viewMode}
          setViewMode={setViewMode}
          loadTasks={loadTasks}
          error={error}
          setError={setError}
          drawerTask={drawerTask}
          setDrawerTask={setDrawerTask}
          editedTask={editedTask}
          setEditedTask={setEditedTask}
          users={users}
          handleSaveTask={handleSaveTask}
          handleDeleteTask={handleDeleteTask}
          canDeleteTasks={canDeleteTasks}
          history={history}
          loadingHistory={loadingHistory}
          historyFilter={historyFilter}
          setHistoryFilter={setHistoryFilter}
          progressUpdate={progressUpdate}
          setProgressUpdate={setProgressUpdate}
          savingProgress={savingProgress}
          handleRecordProgress={handleRecordProgress}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          newTask={newTask}
          setNewTask={setNewTask}
          saveNewTask={saveNewTask}
          notification={notification}
          setNotification={setNotification}
          />
        </Suspense>
        {quickProgressDialog}
      </>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      {!embedded && <Paper
        elevation={0}
        sx={{
          p: { xs: 1.4, sm: 1.7 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #135cb4 0%, #102f66 100%)',
          color: 'white',
          '& .MuiInputLabel-shrink': { bgcolor: 'background.paper', px: 0.5 },
          border: 'none'
        }}
      >
        <Box sx={{ display: "flex", gap: 1.2, flexDirection: "column", alignItems: "stretch" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 2, p: 1, display: 'flex' }}>
                <ChecklistIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{projectId ? 'Actividades del proyecto' : 'Tareas y seguimiento'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Estatus, fecha expectativa y comentarios en un solo registro de trabajo.
                </Typography>
              </Box>
            </Box>
            {isMobile && !showMyDay && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>Nuevo</Button>}
          </Box>

          {!showMyDay && (isMobile ? (
            <Accordion sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FilterListIcon /><Typography>Filters</Typography></Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, '& .MuiTextField-root': { bgcolor: 'background.paper', borderRadius: 1 } }}>
                  <TextField size="small" label="Buscar actividades" value={filters.q} onChange={(e) => setFilters((v) => ({ ...v, q: e.target.value }))} />
                  {!projectId && (
                    <TextField fullWidth size="small" select value={filters.project} onChange={(e) => setFilters((v) => ({ ...v, project: e.target.value }))} label="Proyecto">
                      <MenuItem value="">All projects</MenuItem>
                      <MenuItem value="internal">Internal tasks only</MenuItem>
                      {projects.map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
                    </TextField>
                  )}
                  <TextField fullWidth size="small" select value={filters.assignee} onChange={(e) => setFilters((v) => ({ ...v, assignee: e.target.value }))} label="Responsable">
                    <MenuItem value="">All assignees</MenuItem>
                    {assignees.map((assignee) => (<MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>))}
                  </TextField>
                  <TextField fullWidth size="small" select label="Prioridad" value={filters.priority} onChange={(e) => setFilters((v) => ({ ...v, priority: e.target.value }))}><MenuItem value="">Todas</MenuItem>{[['low', 'Baja'], ['medium', 'Media'], ['high', 'Alta'], ['critical', 'Crítica']].map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}</TextField>
                  {canSeeAllTasks && (
                    <TextField fullWidth size="small" select value={viewMode} onChange={(e) => setViewMode(e.target.value)} label="Alcance">
                      <MenuItem value="all">All tasks</MenuItem>
                      <MenuItem value="mine">Only assigned to me</MenuItem>
                    </TextField>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: 'flex-end', '& .MuiTextField-root': { bgcolor: 'background.paper', borderRadius: 1 } }}>
              <TextField size="small" label="Buscar actividades" value={filters.q} onChange={(e) => setFilters((v) => ({ ...v, q: e.target.value }))} />
              {!projectId && (
                <TextField size="small" select value={filters.project} onChange={(e) => setFilters((v) => ({ ...v, project: e.target.value }))} label="Proyecto" sx={{ minWidth: 180 }}>
                  <MenuItem value="">All projects</MenuItem>
                  {/* ✅ ACTUALIZADO: Opción para tareas internas */}
                  <MenuItem value="internal">Internal tasks only</MenuItem>
                  {projects.map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
                </TextField>
              )}
              <TextField size="small" select value={filters.assignee} onChange={(e) => setFilters((v) => ({ ...v, assignee: e.target.value }))} label="Responsable" sx={{ minWidth: 160 }}>
                <MenuItem value="">All</MenuItem>
                {assignees.map((a) => (<MenuItem key={a} value={a}>{a}</MenuItem>))}
              </TextField>
              <TextField size="small" select label="Prioridad" sx={{ minWidth: 140 }} value={filters.priority} onChange={(e) => setFilters((v) => ({ ...v, priority: e.target.value }))}><MenuItem value="">Todas</MenuItem>{[['low', 'Baja'], ['medium', 'Media'], ['high', 'Alta'], ['critical', 'Crítica']].map(([id, label]) => <MenuItem key={id} value={id}>{label}</MenuItem>)}</TextField>
              {canSeeAllTasks && (
                <TextField size="small" select value={viewMode} onChange={(e) => setViewMode(e.target.value)} label="Alcance" sx={{ minWidth: 170 }}>
                  <MenuItem value="all">All tasks</MenuItem>
                  <MenuItem value="mine">Only assigned to me</MenuItem>
                </TextField>
              )}
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>Nuevo pendiente</Button>
            </Box>
          ))}
        </Box>
      </Paper>}

      {workspaceSwitch}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {dayView}
      {!showMyDay && <>
      <TaskActivityOverview summary={boardStats} selected={filters.status} onSelect={selectActivityFilter} visibleCount={filtered.length} />
      {(filters.q || filters.assignee || filters.priority || (!projectId && filters.project)) && <Button onClick={() => setFilters({ q: '', assignee: '', priority: '', project: projectId ? String(projectId) : '', status: 'active' })} sx={{ minHeight: 44, mb: 1 }}>Limpiar filtros</Button>}
      {!isMobile && <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 1.5 }}>
        <Typography fontWeight={750}>{activityFilterLabel(filters.status)}</Typography>
        <ToggleButtonGroup value={presentation} exclusive onChange={(_, value) => { if (value) setPresentation(value); }} aria-label="Vista de actividades" size="small"><ToggleButton value="board" sx={{ minHeight: 44, px: 2 }}>Tablero</ToggleButton><ToggleButton value="list" sx={{ minHeight: 44, px: 2 }}>Lista</ToggleButton></ToggleButtonGroup>
      </Stack>}

      {/* Kanban Board */}
      {!isMobile && presentation === 'board' ? <TaskActivityBoard tasks={filtered} selected={filters.status} onOpen={setDrawerTask} now={activityNow} /> : isMobile ? (
        <Box>
          <Typography variant="subtitle1" fontWeight={750} sx={{ mb: 1 }}>{activityFilterLabel(filters.status)}</Typography>
          {!filtered.length && <Alert severity="info">No hay actividades con los filtros seleccionados.</Alert>}
          <Stack spacing={1}>{filtered.map((task) => <TaskActivityCard key={task.id} task={task} onOpen={setDrawerTask} now={activityNow} />)}</Stack>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            overflow: 'hidden'
          }}
        >
          <Table sx={{ tableLayout: 'fixed' }} aria-label="Lista de pendientes">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.14 : 0.07) }}>
                <TableCell sx={{ width: 70, fontWeight: 800 }}>ID</TableCell>
                <TableCell sx={{ width: '28%', fontWeight: 800 }}>Pendiente</TableCell>
                <TableCell sx={{ width: 150, fontWeight: 800 }}>Estatus</TableCell>
                <TableCell sx={{ width: 160, fontWeight: 800 }}>Fecha expectativa</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Comentarios</TableCell>
                <TableCell align="right" sx={{ width: 185, fontWeight: 800 }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No hay pendientes con los filtros seleccionados.
                  </TableCell>
                </TableRow>
              ) : filtered.map((task) => {
                const statusDefinition = STATUS_COLUMNS.find((item) => item.id === task.status);
                const overdue = activityDueState(task, activityNow) === 'overdue';
                return (
                  <TableRow key={task.id} hover>
                    <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {task.id}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Button
                        variant="text"
                        onClick={() => setDrawerTask(task)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                          fontWeight: 750,
                          lineHeight: 1.4,
                          overflowWrap: 'anywhere'
                        }}
                      >
                        {task.title}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                        {task.project_name || 'Interno / sin proyecto'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Chip
                        size="small"
                        label={statusDefinition?.title || task.status}
                        sx={{
                          fontWeight: 700,
                          color: STATUS_STYLES[task.status]?.accent,
                          bgcolor: alpha(STATUS_STYLES[task.status]?.accent || '#607d8b', 0.12)
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography variant="body2" color={overdue ? 'error.main' : 'text.primary'} fontWeight={overdue ? 700 : 500}>
                        {formatTaskDate(task.due_date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography
                        variant="body2"
                        color={task.last_comment ? 'text.primary' : 'text.secondary'}
                        sx={{
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.45,
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 3
                        }}
                      >
                        {task.last_comment || 'Sin comentarios todavía'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditNoteOutlinedIcon />}
                        onClick={() => setDrawerTask(task)}
                        sx={{ minHeight: 40, textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Registrar avance
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      </>}
      {quickProgressDialog}
      {/* Task Drawer & Comments */}
      <Drawer
        anchor="right"
        open={!!drawerTask}
        onClose={() => setDrawerTask(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500 },
            pt: 'env(safe-area-inset-top, 0px)',
            pb: 'env(safe-area-inset-bottom, 0px)',
            boxSizing: 'border-box'
          }
        }}
      >
        {editedTask && (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Pendiente #{editedTask.id}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>{editedTask.title}</Typography>
              </Box>
              <IconButton aria-label="Close task details" onClick={() => setDrawerTask(null)}><CloseIcon /></IconButton>
            </Box>
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">Situación actual</Typography>
                    <Typography variant="body2" fontWeight={700} noWrap>{editedTask.assigned_to_name || 'Sin responsable'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                    <Chip
                      size="small"
                      label={getStatusTitle(editedTask.status)}
                      sx={{ fontWeight: 700, color: STATUS_STYLES[editedTask.status]?.accent }}
                    />
                    <Chip size="small" variant="outlined" icon={<AccessTimeIcon />} label={formatTaskDate(editedTask.due_date)} />
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                component="section"
                aria-labelledby="record-progress-title"
                variant="outlined"
                sx={{ p: 2, borderRadius: 2.5, borderColor: alpha(theme.palette.primary.main, 0.35) }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography id="record-progress-title" variant="subtitle1" fontWeight={800}>
                      Registrar avance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Actualiza estatus, fecha expectativa y comentario en una sola acción.
                    </Typography>
                  </Box>
                  <FormControl fullWidth>
                    <InputLabel id={`${progressStatusId}-label`}>Estatus</InputLabel>
                    <Select
                      id={progressStatusId}
                      labelId={`${progressStatusId}-label`}
                      value={progressUpdate.status}
                      label="Estatus"
                      onChange={(event) => setProgressUpdate((current) => ({ ...current, status: event.target.value }))}
                    >
                      {STATUS_COLUMNS.map((status) => (
                        <MenuItem key={status.id} value={status.id}>{status.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <DatePicker
                    label="Fecha expectativa"
                    value={progressUpdate.expected_date}
                    onChange={(value) => setProgressUpdate((current) => ({ ...current, expected_date: value }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    label="Comentario del avance"
                    value={progressUpdate.comment}
                    onChange={(event) => setProgressUpdate((current) => ({ ...current, comment: event.target.value }))}
                    helperText="Describe qué cambió, qué falta o por qué está detenido."
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={savingProgress ? <CircularProgress size={18} color="inherit" /> : <EditNoteOutlinedIcon />}
                    onClick={handleRecordProgress}
                    disabled={savingProgress || !progressUpdate.comment.trim()}
                    sx={{ minHeight: 44 }}
                  >
                    {savingProgress ? 'Guardando avance…' : 'Guardar avance'}
                  </Button>
                </Stack>
              </Paper>

              <Accordion disableGutters variant="outlined" sx={{ borderRadius: '12px !important', '&::before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="task-general-data-content" id="task-general-data-header">
                  <Box>
                    <Typography fontWeight={750}>Datos generales</Typography>
                    <Typography variant="caption" color="text.secondary">Título, descripción, proyecto y responsable</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails id="task-general-data-content">
                  <Stack spacing={2}>
                    <TextField fullWidth label="Pendiente" value={editedTask.title} onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })} />
                    <TextField fullWidth multiline rows={4} label="Descripción" value={editedTask.description || ''} onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })} />
                    <TaskProjectAutocomplete
                      projects={projects}
                      value={editedTask.project_id}
                      onChange={(projectValue) => setEditedTask({ ...editedTask, project_id: projectValue })}
                      loading={projectsLoading}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Responsable</InputLabel>
                      <Select value={editedTask.assigned_to || ''} label="Responsable" onChange={(e) => setEditedTask({ ...editedTask, assigned_to: e.target.value })}>
                        <MenuItem value="">Sin asignar</MenuItem>
                        {users.map((u) => (<MenuItem key={u.id} value={u.id}>{u.first_name || u.username}</MenuItem>))}
                      </Select>
                    </FormControl>
                    <Button fullWidth variant="outlined" startIcon={<SaveIcon />} onClick={handleSaveTask}>Guardar datos generales</Button>
                    {canDeleteTasks && (
                      <Button fullWidth variant="text" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteTask}>Eliminar pendiente</Button>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Box component="section" aria-labelledby="task-history-title">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HistoryOutlinedIcon color="primary" />
                      <Typography id="task-history-title" variant="subtitle1" fontWeight={800}>Historial</Typography>
                      <Chip size="small" label={history.length} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Registro permanente de avances y cambios.</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }}>
                  {[
                    ['all', 'Todo'],
                    ['progress', 'Avances'],
                    ['changes', 'Cambios']
                  ].map(([value, label]) => (
                    <Chip
                      key={value}
                      clickable
                      label={label}
                      color={historyFilter === value ? 'primary' : 'default'}
                      variant={historyFilter === value ? 'filled' : 'outlined'}
                      onClick={() => setHistoryFilter(value)}
                      sx={{ minHeight: 36 }}
                    />
                  ))}
                </Stack>
                <Box sx={{ maxHeight: 430, overflowY: 'auto', pr: 0.5 }}>
                  {loadingHistory ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress size={22} /></Box>
                  ) : filteredHistory.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
                      <HistoryOutlinedIcon color="disabled" />
                      <Typography variant="body2" color="text.secondary">No hay eventos en este filtro.</Typography>
                    </Paper>
                  ) : filteredHistory.map((event, index) => {
                    const changes = parseHistoryChanges(event.changes_json);
                    const detailChanges = Object.entries(changes).filter(([field, change]) => (
                      !['status', 'due_date'].includes(field)
                      && change
                      && typeof change === 'object'
                      && Object.prototype.hasOwnProperty.call(change, 'previous')
                    ));
                    const statusChanged = event.previous_status && event.new_status && event.previous_status !== event.new_status;
                    const previousDate = event.previous_expected_date ? String(event.previous_expected_date).slice(0, 10) : null;
                    const newDate = event.new_expected_date ? String(event.new_expected_date).slice(0, 10) : null;
                    const dateChanged = previousDate !== newDate && (previousDate || newDate);
                    const accent = STATUS_STYLES[event.new_status]?.accent || theme.palette.primary.main;
                    return (
                      <Box key={event.id} sx={{ position: 'relative', pl: 4.5, pb: index === filteredHistory.length - 1 ? 0 : 2.25 }}>
                        {index !== filteredHistory.length - 1 && (
                          <Box sx={{ position: 'absolute', left: 15, top: 28, bottom: -3, width: 2, bgcolor: 'divider' }} />
                        )}
                        <Avatar sx={{ position: 'absolute', left: 0, top: 0, width: 32, height: 32, bgcolor: alpha(accent, 0.16), color: accent, fontSize: 13, fontWeight: 800 }}>
                          {(event.actor_name || 'S').charAt(0).toUpperCase()}
                        </Avatar>
                        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.25 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {event.actor_name || 'Sistema'} <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{HISTORY_EVENT_LABELS[event.event_type] || 'actualizó el pendiente'}</Box>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{formatHistoryDateTime(event.created_at)}</Typography>
                          {event.comment && (
                            <Typography variant="body2" sx={{ mt: 1.25, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.comment}</Typography>
                          )}
                          {(statusChanged || dateChanged) && (
                            <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                              {statusChanged && (
                                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                                  <Chip size="small" variant="outlined" label={getStatusTitle(event.previous_status)} />
                                  <ArrowForwardRoundedIcon fontSize="small" color="disabled" />
                                  <Chip size="small" label={getStatusTitle(event.new_status)} sx={{ fontWeight: 700, color: accent }} />
                                </Stack>
                              )}
                              {dateChanged && (
                                <Typography variant="caption" color="text.secondary">
                                  Fecha expectativa: <strong>{formatTaskDate(previousDate)}</strong> → <strong>{formatTaskDate(newDate)}</strong>
                                </Typography>
                              )}
                            </Stack>
                          )}
                          {event.event_type === 'details_updated' && detailChanges.length > 0 && (
                            <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                              {detailChanges.map(([field, change]) => (
                                <Typography key={field} variant="caption" component="div" sx={{ mb: 0.5, overflowWrap: 'anywhere' }}>
                                  <Box component="span" color="text.secondary">{change.label || field}: </Box>
                                  {formatHistoryValue(field, change.previous)} → <strong>{formatHistoryValue(field, change.next)}</strong>
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Stack>
          </Box>
        )}
      </Drawer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Nuevo pendiente</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Pendiente" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} required />
            <TextField fullWidth multiline rows={3} label="Descripción" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
            <TaskProjectAutocomplete
              projects={projects}
              value={newTask.project_id}
              onChange={(projectValue) => setNewTask({ ...newTask, project_id: projectValue })}
              loading={projectsLoading}
            />
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select value={newTask.assigned_to} label="Assigned To" onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}>
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((u) => (<MenuItem key={u.id} value={u.id}>{u.first_name || u.username}</MenuItem>))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Estatus inicial</InputLabel>
              <Select value={newTask.status} label="Estatus inicial" onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}>
                {STATUS_COLUMNS.map((statusOption) => (
                  <MenuItem key={statusOption.id} value={statusOption.id}>{statusOption.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select value={newTask.priority} label="Priority" onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <DatePicker label="Fecha expectativa" value={newTask.due_date} onChange={(newValue) => setNewTask({ ...newTask, due_date: newValue })} slotProps={{ textField: { fullWidth: true } }} />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Comentario inicial"
              value={newTask.initial_comment}
              onChange={(e) => setNewTask({ ...newTask, initial_comment: e.target.value })}
              helperText="Opcional: contexto, dependencia o siguiente paso conocido."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNewTask} startIcon={<AddIcon />}>Crear pendiente</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notification} autoHideDuration={5000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setNotification(null)} severity={notification?.severity || 'info'} sx={{ width: '100%' }}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
